import type { StatusDefinitionRow } from '../statusDefinitions';

/**
 * The Lead (piece 20): a claim before it is a relationship. 🔨 status,
 * temperature and stage answer three different questions and none derives
 * from another; status moves only through the action that causes each
 * state, temperature through a recorded outbound attempt, stage through a
 * gate whose questions are checked over non-empty notes.
 */
export const LEAD_TEMPERATURES = ['Cold', 'Warm', 'Hot'] as const;
export type LeadTemperature = (typeof LEAD_TEMPERATURES)[number];

export const LEAD_SOURCES = ['Website', 'ManualEntry', 'TextMessage'] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const LEAD_STATUSES = ['New', 'Working', 'Held', 'Blocked', 'NotAFit', 'ConvertedToCompany', 'ConvertedToDeal'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_STAGES = ['Awareness', 'Interest', 'Negotiation'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

export const ABSENCE_KINDS = ['Away', 'Extended', 'DoNotContact'] as const;
export const ABSENCE_SOURCES = ['Manual', 'DetectedFromAutoReply'] as const;

/** The StatusTransition rows this piece seeds for LeadStatus; canTransition reads them and a move the table does not carry is refused. */
export const LEAD_STATUS_TRANSITIONS: readonly { from: LeadStatus; to: LeadStatus }[] = [
  { from: 'New', to: 'Working' },
  { from: 'New', to: 'Held' },
  { from: 'Working', to: 'Held' },
  { from: 'Held', to: 'New' },
  { from: 'New', to: 'Blocked' },
  { from: 'Working', to: 'Blocked' },
  { from: 'New', to: 'NotAFit' },
  { from: 'Working', to: 'NotAFit' },
  { from: 'New', to: 'ConvertedToCompany' },
  { from: 'Working', to: 'ConvertedToCompany' },
  { from: 'New', to: 'ConvertedToDeal' },
  { from: 'Working', to: 'ConvertedToDeal' },
  { from: 'ConvertedToCompany', to: 'ConvertedToDeal' },
];

export function leadTransitionAllowed(from: LeadStatus, to: LeadStatus): boolean {
  return LEAD_STATUS_TRANSITIONS.some((row) => row.from === from && row.to === to);
}

/** One StatusDefinition row per member of the six enums: Held warning, Blocked and DoNotContact error, NotAFit and Cold inactive, the rest good. */
export const LEAD_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = [
  ...LEAD_STATUSES.map((value, i): StatusDefinitionRow => ({
    entityType: 'LeadStatus',
    statusValue: value,
    label: value === 'NotAFit' ? 'Not a Fit' : value === 'ConvertedToCompany' ? 'Converted to Company' : value === 'ConvertedToDeal' ? 'Converted to Deal' : value,
    colorGroup: value === 'Held' ? 'warning' : value === 'Blocked' ? 'error' : value === 'NotAFit' ? 'inactive' : 'good',
    sortOrder: i + 1,
  })),
  ...LEAD_TEMPERATURES.map((value, i): StatusDefinitionRow => ({ entityType: 'LeadTemperature', statusValue: value, label: value, colorGroup: value === 'Cold' ? 'inactive' : 'good', sortOrder: i + 1 })),
  ...LEAD_STAGES.map((value, i): StatusDefinitionRow => ({ entityType: 'LeadStage', statusValue: value, label: value, colorGroup: 'good', sortOrder: i + 1 })),
  ...LEAD_SOURCES.map((value, i): StatusDefinitionRow => ({ entityType: 'LeadSource', statusValue: value, label: value === 'ManualEntry' ? 'Manual Entry' : value === 'TextMessage' ? 'Text Message' : value, colorGroup: 'good', sortOrder: i + 1 })),
  ...ABSENCE_KINDS.map((value, i): StatusDefinitionRow => ({ entityType: 'AbsenceKind', statusValue: value, label: value === 'DoNotContact' ? 'Do Not Contact' : value, colorGroup: value === 'DoNotContact' ? 'error' : 'good', sortOrder: i + 1 })),
  ...ABSENCE_SOURCES.map((value, i): StatusDefinitionRow => ({ entityType: 'AbsenceSource', statusValue: value, label: value === 'DetectedFromAutoReply' ? 'Detected from Auto-Reply' : value, colorGroup: 'good', sortOrder: i + 1 })),
];

/** The Interest card's two questions by position in the seed (decision 10881): Lead conversion reads the stated need and the next contact by these, not by literal numbers. */
export const INTEREST_NEED_QUESTION_ORDER = 1;
export const INTEREST_NEXT_CONTACT_QUESTION_ORDER = 2;

/** The four seeded question pairs (decision 0255): the label states what must be true, the placeholder shows a good answer. */
export const LEAD_STAGE_QUESTION_SEED: readonly { stage: LeadStage; sortOrder: number; label: string; placeholder: string }[] = [
  { stage: 'Awareness', sortOrder: 1, label: 'We know what this prospect does and what they buy', placeholder: 'Fabricator, two shifts, mostly stainless; buys scales and calibration' },
  { stage: 'Awareness', sortOrder: 2, label: 'We know how this prospect reached us', placeholder: 'Web form after the Pittsburgh show, asking about calibration' },
  { stage: 'Interest', sortOrder: INTEREST_NEED_QUESTION_ORDER, label: 'A specific need is stated in the prospect’s own words', placeholder: 'Their bench scale fails calibration twice a year and stops the line' },
  { stage: 'Interest', sortOrder: INTEREST_NEXT_CONTACT_QUESTION_ORDER, label: 'We know who to speak to next and when', placeholder: 'Maintenance manager Dan, calling back Tuesday after their shutdown' },
];

/**
 * Negotiation's three items: NDA executed is derived from NDAAgreement, Rate
 * Sheet agreed is ticked over a note on the Lead alone, and MSA signed takes
 * the signed file itself (decision 0332): the upload is the answer, and the
 * file's name stands as the note the gate reads.
 */
export const NEGOTIATION_ITEMS: readonly { key: 'NdaExecuted' | 'RateSheetAgreed' | 'MsaSigned'; label: string; placeholder: string; derived: boolean; takesFile: boolean }[] = [
  { key: 'NdaExecuted', label: 'NDA executed', placeholder: 'Read from the Company’s Active NDA; upload creates Draft and a Draft opens nothing', derived: true, takesFile: false },
  { key: 'RateSheetAgreed', label: 'Rate Sheet agreed', placeholder: 'Which sheet, agreed with whom and when', derived: false, takesFile: false },
  { key: 'MsaSigned', label: 'MSA signed where applicable', placeholder: 'Upload the signed MSA, or mark why none applies', derived: false, takesFile: true },
];

export interface QuestionAnswer {
  checked: boolean;
  notes?: string | null;
}

/** canCompleteStage on a Lead card: every question checked and carrying non-empty notes; a checked box alone never counts. */
export function leadStageComplete(rows: readonly QuestionAnswer[]): { ok: boolean; problems: string[] } {
  const problems: string[] = [];
  rows.forEach((row, i) => {
    if (!row.checked) problems.push(`question ${i + 1} is not checked`);
    else if (!row.notes || row.notes.trim() === '') problems.push(`question ${i + 1} is checked over a blank note`);
  });
  return { ok: problems.length === 0, problems };
}

/** Stage progress is a checked-count-over-total label, computed on read and never stored. */
export function stageProgressLabel(rows: readonly QuestionAnswer[]): string {
  return `${rows.filter((r) => r.checked).length}/${rows.length}`;
}

export function nextLeadStage(stage: LeadStage): LeadStage | null {
  const i = LEAD_STAGES.indexOf(stage);
  return i >= 0 && i < LEAD_STAGES.length - 1 ? LEAD_STAGES[i + 1]! : null;
}

/** The stage Back moves the Lead to (decision 0331): Interest and Negotiation step back one; Awareness has nothing behind it. Its cards stay. */
export function previousLeadStage(stage: LeadStage): LeadStage | null {
  const i = LEAD_STAGES.indexOf(stage);
  return i > 0 ? LEAD_STAGES[i - 1]! : null;
}

/**
 * Which of a Lead's cards take input (decision 0331): the current stage, every
 * stage behind it, and Interest while Awareness is current, so the second
 * card fills as the first is worked. 🔨 Completing a stage still needs the
 * stage before it complete; only the editing widens, never the gate.
 */
export function leadCardEditable(card: LeadStage, current: LeadStage): boolean {
  const at = LEAD_STAGES.indexOf(card);
  const now = LEAD_STAGES.indexOf(current);
  if (at <= now) return true;
  return card === 'Interest' && current === 'Awareness';
}

/** 🔨 The NDA item reads NDAAgreement.status Active with the dates in force, never a linked File (decision 0093). */
export function ndaSatisfied(agreement: { status: string; effectiveDate: Date | string; expiryDate?: Date | string | null } | null, today: Date): boolean {
  if (!agreement || agreement.status !== 'Active') return false;
  const effective = new Date(agreement.effectiveDate);
  if (today.getTime() < effective.getTime()) return false;
  if (!agreement.expiryDate) return true;
  return today.getTime() <= new Date(agreement.expiryDate).getTime();
}

/** 🔨 Log Call Attempt moves Cold to Warm and nothing else; Hot is a positive two-way response a person records. */
export function temperatureAfterCall(current: LeadTemperature): LeadTemperature {
  return current === 'Cold' ? 'Warm' : current;
}

export function notFitProblem(reason: string | null | undefined): string | null {
  return reason && reason.trim() ? null : 'marking a Lead Not a Fit needs a non-blank reason';
}

/** The override to convert over an incomplete Negotiation is offered once the Lead carries the configured number of Log Call Attempt rows (decision 0254). */
export function conversionOverrideOffered(callAttempts: number, threshold: number | null | undefined): boolean {
  return callAttempts >= (typeof threshold === 'number' && threshold > 0 ? threshold : 3);
}

export function conversionProblem(input: { negotiationComplete: boolean; override: { reason: string | null | undefined; offered: boolean } | null }): string | null {
  if (input.negotiationComplete) return null;
  if (!input.override) return 'the Lead’s Negotiation stage is not complete; an override is offered once enough call attempts are logged';
  if (!input.override.offered) return 'the override is not offered yet: log more call attempts first';
  if (!input.override.reason || !input.override.reason.trim()) return 'the override needs a typed reason; type why this Lead converts with its Negotiation stage incomplete';
  return null;
}

/** The first logged contact or the first answered question writes Working; nothing else moves New forward. */
export function statusOnFirstWork(status: LeadStatus): LeadStatus {
  return status === 'New' ? 'Working' : status;
}
